import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";
import { VIDEOS, FLYERS } from "@/data/site";
import { Reveal, MaskedLine, ChapterLabel } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

function VideoCard({ video, index, onPlay }) {
  return (
    <Reveal delay={index * 0.1}>
      <motion.button
        whileHover={{ y: -8 }}
        onClick={() => onPlay(video)}
        data-testid={`video-card-${video.id}`}
        className="group relative w-full text-left bg-[#0b1838] border border-cyan-400/15 rounded-2xl overflow-hidden hover:border-cyan-400/50 transition-colors"
      >
        <div className="relative aspect-video overflow-hidden">
          <video src={video.url} muted playsInline preload="metadata" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-[opacity,transform] duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030712]/90 via-transparent to-transparent" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="w-16 h-16 rounded-full bg-cyan-400 text-[#030712] flex items-center justify-center group-hover:scale-110 transition-transform shadow-[0_0_40px_rgba(0,240,255,0.5)]">
              <Play className="w-7 h-7 ml-1" fill="currentColor" />
            </span>
          </span>
        </div>
        <div className="p-6">
          <p className="font-mono-brand text-[10px] tracking-[0.3em] uppercase text-cyan-400/70 mb-2">Video {String(index + 1).padStart(2, "0")}</p>
          <h3 className="font-display font-bold uppercase text-2xl tracking-tight">{video.title}</h3>
          <p className="mt-2 text-sm text-slate-400 leading-relaxed">{video.description}</p>
        </div>
      </motion.button>
    </Reveal>
  );
}

export default function Gallery() {
  const [active, setActive] = useState(null);

  return (
    <main data-testid="gallery-page">
      <section className="relative pt-40 pb-24 bg-[#060f26] border-b border-cyan-400/10 grain-overlay">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <MaskedLine delay={0.1}>
            <span className="font-mono-brand text-xs sm:text-sm tracking-[0.35em] uppercase text-cyan-400 font-bold">Work Showcase</span>
          </MaskedLine>
          <h1 className="mt-6 font-display font-black italic uppercase tracking-tighter leading-[0.9] text-5xl sm:text-7xl lg:text-8xl" data-testid="gallery-title">
            <MaskedLine delay={0.25}>Watch Grime</MaskedLine>
            <MaskedLine delay={0.4} className="text-cyan-400">Disappear.</MaskedLine>
          </h1>
        </div>
      </section>

      <Marquee />

      <section className="py-24 sm:py-32" data-testid="video-gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <ChapterLabel number="01" text="In Action" />
            <h2 className="font-display font-extrabold uppercase tracking-tight leading-none text-4xl sm:text-6xl">
              Real Jobs. <span className="text-cyan-400 italic">Real Pressure.</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {VIDEOS.map((v, i) => (
              <VideoCard key={v.id} video={v} index={i} onPlay={setActive} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-[#060f26] border-t border-cyan-400/10" data-testid="photo-gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <ChapterLabel number="02" text="Before & After" />
            <h2 className="font-display font-extrabold uppercase tracking-tight leading-none text-4xl sm:text-6xl">
              The Proof Is <span className="text-cyan-400 italic">On The Surface</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-10">
            {[FLYERS.main, FLYERS.services].map((src, i) => (
              <Reveal key={src} delay={i * 0.12}>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 200, damping: 20 }}
                  className="clip-frame border border-cyan-400/20 overflow-hidden shadow-[0_0_60px_rgba(0,240,255,0.08)]"
                  data-testid={`gallery-flyer-${i + 1}`}
                >
                  <img src={src} alt={`Before and after pressure washing results ${i + 1}`} className="w-full h-auto" loading="lazy" />
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#030712]/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-10"
            onClick={() => setActive(null)}
            data-testid="video-modal"
          >
            <motion.div
              initial={{ scale: 0.92, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.92, y: 24 }}
              transition={{ type: "spring", stiffness: 220, damping: 24 }}
              className="relative w-full max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setActive(null)}
                data-testid="video-modal-close"
                aria-label="Close video"
                className="absolute -top-12 right-0 text-slate-300 hover:text-cyan-400 transition-colors"
              >
                <X className="w-8 h-8" />
              </button>
              <video src={active.url} controls autoPlay playsInline className="w-full rounded-2xl border border-cyan-400/30 shadow-[0_0_80px_rgba(0,240,255,0.15)]" data-testid="video-modal-player" />
              <p className="mt-4 font-display font-bold uppercase text-xl text-white">{active.title}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
